import { useEffect, useRef, useState } from 'react'
import { Button, Drawer, Input, Tag, Avatar, Tooltip } from 'antd'
import { RobotOutlined, SendOutlined, ThunderboltOutlined, CloseOutlined } from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { NLP_SAMPLES, CLEARANCE_LABEL, CLEARANCE_COLOR } from '@/mocks'
import type { NLPSampleResponse } from '@/mocks'

interface ChatMessage {
  id: string
  role: 'user' | 'ai'
  content: string
  source?: string[]
  relatedActions?: NLPSampleResponse['relatedActions']
  clearance?: NLPSampleResponse['clearance']
  timestamp: number
}

function matchResponse(query: string): NLPSampleResponse | null {
  const q = query.toLowerCase().trim()
  if (!q) return null
  // best match: highest token overlap
  let best: { score: number; sample: NLPSampleResponse } | null = null
  for (const s of NLP_SAMPLES) {
    const tokens = q.split(/\s+/)
    const ref = s.query.toLowerCase()
    let score = 0
    for (const t of tokens) if (ref.includes(t)) score++
    if (!best || score > best.score) best = { score, sample: s }
  }
  return best && best.score >= 2 ? best.sample : null
}

const SUGGESTED_PROMPTS = [
  'Tuần này có phiên bay nào chở vũ khí?',
  'Giãn cách bay F-16 với Su-30 là bao nhiêu?',
  'Phi công nào sắp hết hạn giấy phép y tế?',
  'Có target nào trong AOR-3 không?',
  'Tình hình khí tượng sân Đà Nẵng?',
]

export function NLPAssistant() {
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'ai',
      content:
        'Em là **CTK Assistant** — AI trợ lý hệ thống PK-KQ. Anh có thể hỏi em về phiên bay, phi công, quy chế, khí tượng, target radar... Em sẽ tìm dữ liệu giúp anh.',
      timestamp: Date.now(),
    },
  ])
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const send = (text?: string) => {
    const q = (text ?? input).trim()
    if (!q) return
    const userMsg: ChatMessage = {
      id: 'm' + Date.now(),
      role: 'user',
      content: q,
      timestamp: Date.now(),
    }
    setMessages((prev) => [...prev, userMsg])
    setInput('')

    setTimeout(() => {
      const match = matchResponse(q)
      const reply: ChatMessage = match
        ? {
            id: 'm' + Date.now() + '-r',
            role: 'ai',
            content: match.answer,
            source: match.source,
            relatedActions: match.relatedActions,
            clearance: match.clearance,
            timestamp: Date.now(),
          }
        : {
            id: 'm' + Date.now() + '-r',
            role: 'ai',
            content:
              'Em chưa có dữ liệu cho câu hỏi này. Anh thử hỏi rõ hơn hoặc chọn 1 trong các gợi ý phía dưới nhé.',
            timestamp: Date.now(),
          }
      setMessages((prev) => [...prev, reply])
    }, 420)
  }

  const renderContent = (content: string) => {
    // simple markdown bold
    const parts = content.split(/(\*\*[^*]+\*\*)/g)
    return parts.map((p, i) =>
      p.startsWith('**') && p.endsWith('**') ? (
        <strong key={i} className="text-[var(--color-text-primary)]">
          {p.slice(2, -2)}
        </strong>
      ) : (
        <span key={i}>{p}</span>
      ),
    )
  }

  return (
    <>
      <Tooltip title="NLP Assistant — Trợ lý hội thoại AI" placement="left">
        <Button
          type="primary"
          shape="circle"
          size="large"
          icon={<RobotOutlined />}
          onClick={() => setOpen(true)}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 999,
            width: 56,
            height: 56,
            background: 'linear-gradient(135deg, #a855f7, #7c3aed)',
            borderColor: '#a855f7',
            boxShadow: '0 8px 24px rgba(168, 85, 247, 0.35)',
          }}
        />
      </Tooltip>

      <Drawer
        title={
          <div className="flex items-center gap-2">
            <Avatar
              size={32}
              icon={<RobotOutlined />}
              style={{ background: 'linear-gradient(135deg, #a855f7, #7c3aed)' }}
            />
            <div>
              <div className="text-[14px] font-semibold leading-tight">CTK Assistant</div>
              <div className="text-[10px] text-[var(--color-text-muted)]">
                NLP-ASR-V3 · Mức mật: Confidential
              </div>
            </div>
            <Tag color="purple" className="!m-0 !ml-2 !text-[10px]">
              AI
            </Tag>
          </div>
        }
        placement="right"
        size={460}
        open={open}
        onClose={() => setOpen(false)}
        closeIcon={<CloseOutlined />}
        styles={{
          body: { padding: 0, display: 'flex', flexDirection: 'column' },
        }}
      >
        <div className="flex-1 overflow-auto p-4 space-y-3">
          {messages.map((m) => (
            <div
              key={m.id}
              className={`flex gap-2 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}
            >
              <Avatar
                size={28}
                icon={m.role === 'ai' ? <RobotOutlined /> : null}
                style={{
                  background:
                    m.role === 'ai'
                      ? 'linear-gradient(135deg, #a855f7, #7c3aed)'
                      : '#1e6091',
                  flexShrink: 0,
                }}
              >
                {m.role === 'user' ? 'A' : null}
              </Avatar>
              <div
                className="max-w-[78%] rounded-lg px-3 py-2"
                style={{
                  background:
                    m.role === 'ai'
                      ? 'rgba(168,85,247,0.08)'
                      : 'rgba(30,96,145,0.18)',
                  border:
                    m.role === 'ai'
                      ? '1px solid rgba(168,85,247,0.25)'
                      : '1px solid rgba(30,96,145,0.35)',
                }}
              >
                <div className="text-[13px] text-[var(--color-text-secondary)] leading-relaxed">
                  {renderContent(m.content)}
                </div>
                {m.source && m.source.length > 0 && (
                  <div className="mt-2 flex items-center gap-1 flex-wrap">
                    <span className="text-[10px] text-[var(--color-text-muted)]">
                      Nguồn:
                    </span>
                    {m.source.map((s, i) => (
                      <Tag key={i} className="!text-[10px] !m-0">
                        {s}
                      </Tag>
                    ))}
                    {m.clearance && (
                      <Tag color={CLEARANCE_COLOR[m.clearance]} className="!text-[10px] !m-0">
                        {CLEARANCE_LABEL[m.clearance]}
                      </Tag>
                    )}
                  </div>
                )}
                {m.relatedActions && m.relatedActions.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {m.relatedActions.map((a, i) => (
                      <Link
                        key={i}
                        to={a.path}
                        onClick={() => setOpen(false)}
                        className="block text-[12px] text-[var(--color-primary-400)] hover:underline"
                      >
                        → {a.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {messages.length <= 1 && (
          <div className="px-4 pb-2 border-t border-[var(--color-border-subtle)] pt-3">
            <div className="text-[11px] text-[var(--color-text-muted)] mb-2 flex items-center gap-1">
              <ThunderboltOutlined /> Gợi ý câu hỏi
            </div>
            <div className="flex flex-wrap gap-1.5">
              {SUGGESTED_PROMPTS.map((p) => (
                <Tag
                  key={p}
                  className="!cursor-pointer !text-[11px] !mb-1"
                  onClick={() => send(p)}
                >
                  {p}
                </Tag>
              ))}
            </div>
          </div>
        )}

        <div className="p-3 border-t border-[var(--color-border-subtle)]">
          <Input.Search
            placeholder="Hỏi em điều gì..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onSearch={() => send()}
            enterButton={<SendOutlined />}
            size="middle"
          />
          <div className="text-[10px] text-[var(--color-text-muted)] mt-1.5 text-center">
            AI có thể sai — hãy xác minh với nguồn chính thức cho quyết định quan trọng
          </div>
        </div>
      </Drawer>
    </>
  )
}
